import React from "react";
import { useAlerts, useClearAlerts, useMarkAlertsSeen } from "@/lib/queries";
import { formatPrice, formatRelative, RETAILER_COLORS } from "@/lib/cr";
import { ExternalLink, Trash2, CheckCheck, Bell } from "lucide-react";
import { toast } from "sonner";

export default function AlertsPage() {
  const { data: alerts = [] } = useAlerts(200);
  const clear = useClearAlerts();
  const markSeen = useMarkAlertsSeen();

  return (
    <div className="p-6 max-w-7xl w-full mx-auto" data-testid="alerts-page">
      <header className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
            Alert <span className="text-[#EAB308]">log</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-1">Everything the scanner flagged for your watches.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="alerts-mark-seen"
            onClick={() => markSeen.mutate(undefined, { onSuccess: () => toast.success("Marked as seen") })}
            className="px-3 py-2 text-xs uppercase font-bold tracking-wider border border-[#262626] hover:border-[#A3A3A3] hover:bg-[#1A1A1A] flex items-center gap-1.5 rounded-sm"
          >
            <CheckCheck className="w-3.5 h-3.5" /> Mark all seen
          </button>
          <button
            data-testid="alerts-clear"
            onClick={() => clear.mutate(undefined, { onSuccess: () => toast.success("Cleared") })}
            className="px-3 py-2 text-xs uppercase font-bold tracking-wider border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 flex items-center gap-1.5 rounded-sm"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear all
          </button>
        </div>
      </header>

      {alerts.length === 0 ? (
        <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-12 text-center rounded-sm">
          <Bell className="w-8 h-8 text-[#737373] mx-auto mb-2" />
          <div className="text-sm text-[#A3A3A3]">No alerts yet.</div>
          <div className="text-xs text-[#525252] mt-1">Create watchlist items and start the scanner.</div>
        </div>
      ) : (
        <div className="border border-[#262626] rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="alerts-table">
            <thead className="bg-[#0d0d0d] border-b border-[#262626]">
              <tr className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">
                <th className="text-left px-4 py-2.5">When</th>
                <th className="text-left px-4 py-2.5">Item</th>
                <th className="text-left px-4 py-2.5">Retailer</th>
                <th className="text-right px-4 py-2.5">Price</th>
                <th className="text-right px-4 py-2.5">Deal</th>
                <th className="text-left px-4 py-2.5">Watch</th>
                <th className="text-right px-4 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => {
                const color = RETAILER_COLORS[a.retailer_id] || "#A3A3A3";
                return (
                  <tr
                    key={a.id}
                    data-testid={`alert-row-${a.id}`}
                    className="border-b border-[#1A1A1A] hover:bg-[#0d0d0d] transition-colors"
                  >
                    <td className="px-4 py-2.5 text-xs font-mono text-[#A3A3A3]">{formatRelative(a.triggered_at)}</td>
                    <td className="px-4 py-2.5 text-sm text-white truncate max-w-xs">{a.title}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className="text-[10px] font-mono font-bold px-2 py-0.5 border rounded-sm"
                        style={{ color, borderColor: `${color}55`, backgroundColor: "#0a0a0a" }}
                      >
                        {a.retailer_name}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold text-white">{formatPrice(a.price)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#EAB308]">{a.deal_score}</td>
                    <td className="px-4 py-2.5 text-xs text-[#A3A3A3]">{a.watch_name || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#EAB308] hover:text-[#FDE047] inline-flex items-center gap-1 text-xs uppercase font-bold tracking-wider"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
